import {
  type ArrangedField,
  type CalendarGrid,
  type CalendarLayout,
  type CalendarStyle,
  DEFAULT_EMBED_LOOK,
  DEFAULT_GRID,
  type DoorNumberStyle,
  type FieldState,
  arrangeFields,
  gridColumnCounts,
} from "@countdown/theme";

import type { ActionDetail } from "@/lib/api";

/**
 * The calendar settings of one action as the screen holds them before they are saved: one
 * draft for the three forms (arrangement, look, door number) and the preview beside them, so
 * the preview shows every unsaved change together and a save of one form keeps the others.
 */
export interface CalendarDraft {
  readonly layout: CalendarLayout;
  /** Columns and tile form set by hand; off means the built-in grid. */
  readonly custom: boolean;
  readonly grid: CalendarGrid;
  readonly style: CalendarStyle;
  /** An own door number; off means the built-in look. */
  readonly numberCustom: boolean;
  readonly number: DoorNumberStyle;
}

/**
 * Where "eigene Darstellung" starts: the built-in font, size and colours, centred on the door
 * and at the top centre of an open one. Those two places stay inside the square and the
 * hexagon alike; the built-in top left corner is cut off by the hexagon.
 */
export const DEFAULT_DOOR_NUMBER: DoorNumberStyle = {
  door: {
    font: "display",
    weight: "bold",
    color: "#1a2332",
    size: 38,
    x: 50,
    y: 50,
    rotation: 0,
    layer: "front",
  },
  open: {
    font: "display",
    weight: "bold",
    color: "#646c79",
    size: 15,
    x: 50,
    y: 14,
    rotation: 0,
    layer: "front",
  },
};

/** The draft of what is saved. */
export function draftOf(detail: ActionDetail): CalendarDraft {
  return {
    layout: detail.calendar_layout,
    custom: detail.calendar_grid != null,
    grid: detail.calendar_grid ?? DEFAULT_GRID,
    style: detail.calendar_style,
    numberCustom: detail.door_number_style != null,
    number: detail.door_number_style ?? DEFAULT_DOOR_NUMBER,
  };
}

/** The bodies of the three writes. Equal bodies mean nothing is left to save. */
export const layoutRequest = (draft: CalendarDraft) => ({
  layout: draft.layout,
  grid: draft.custom ? draft.grid : null,
});
// The frame's look has its own form and write (embed-look); the server keeps the stored one on
// this write, so a constant stands in for it and a saved frame look leaves the doors unchanged.
export const styleRequest = (draft: CalendarDraft) => ({
  style: { ...draft.style, embed: DEFAULT_EMBED_LOOK },
});
export const numberRequest = (draft: CalendarDraft) => ({
  style: draft.numberCustom ? draft.number : null,
});

/** Positions 1, 2 and 3 show a day gone, a day taken part in and today; the rest are to come. */
export function sampleState(position: number): FieldState {
  if (position === 1) {
    return "past_open";
  }
  if (position === 2) {
    return "past_participated";
  }
  return position === 3 ? "today" : "locked";
}

/** The doors of the action in the calendar's own order, empty places as negative positions. */
export function previewArrangement(
  detail: Pick<ActionDetail, "field_count" | "action_id">,
  draft: CalendarDraft,
): ArrangedField[] {
  const positions = Array.from({ length: detail.field_count }, (_, index) => index + 1);
  return arrangeFields(positions, detail.action_id, {
    layout: draft.layout,
    columns: gridColumnCounts(draft.custom ? draft.grid : null),
    fillers: draft.style.filler_count,
  });
}

function sorted(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sorted);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, entry]) => [key, sorted(entry)]),
    );
  }
  return value;
}

/**
 * Whether two values are the same data, whatever the order of their keys: the built-in look
 * and the server's copy of it list their keys differently.
 */
export function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(sorted(a)) === JSON.stringify(sorted(b));
}
